import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faHistory, faTrash, faChartLine, faDownload, 
  faCalendar, faComments, faTimes, faSpinner,
  faPlus, faSyncAlt
} from '@fortawesome/free-solid-svg-icons';
import axiosInstance from '../components/axiosInstance';

const noopAsync = async () => {};
const noopReturnNull = async () => null;

const ChatHistory = ({
  onClose,
  threads = [],
  onSelectThread = noopAsync,
  onCreateThread = noopReturnNull,
  onRefreshThreads = noopAsync,
  isThreadsLoading = false,
  activeThreadId = null
}) => {
  const [conversations, setConversations] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('history'); // 'history' or 'analytics'
  const [limit] = useState(20);
  const [offset, setOffset] = useState(0);
  const [selectedThreadId, setSelectedThreadId] = useState(activeThreadId);

  const fetchHistory = useCallback(async (signal) => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        limit: String(limit),
        offset: String(offset),
      });

      if (selectedThreadId) {
        params.append('thread_id', String(selectedThreadId));
      }

      const response = await axiosInstance.get(
        `chat/history/?${params.toString()}`,
        { signal }
      );
      if (signal && signal.aborted) {
        return;
      }

      setConversations(response.data.conversations);
    } catch (error) {
      if (signal && signal.aborted) {
        return;
      }
      console.error('Error fetching chat history:', error);
    } finally {
      if (!signal || !signal.aborted) {
        setLoading(false);
      }
    }
  }, [limit, offset, selectedThreadId]);

  const fetchAnalytics = useCallback(async (signal) => {
    try {
      const params = new URLSearchParams();
      if (selectedThreadId) {
        params.append('thread_id', String(selectedThreadId));
      }

      const url = params.toString()
        ? `chat/analytics/?${params.toString()}`
        : 'chat/analytics/';

      const response = await axiosInstance.get(url, { signal });
      if (signal && signal.aborted) {
        return;
      }

      setAnalytics(response.data);
    } catch (error) {
      if (signal && signal.aborted) {
        return;
      }
      console.error('Error fetching analytics:', error);
    }
  }, [selectedThreadId]);

  const selectedThread = useMemo(() => {
    if (!selectedThreadId) {
      return null;
    }
    return threads.find((thread) => thread.id === selectedThreadId) || null;
  }, [selectedThreadId, threads]);

  useEffect(() => {
    const controller = new AbortController();
    const fetchData = async () => {
      await fetchHistory(controller.signal);
      await fetchAnalytics(controller.signal);
    };

    fetchData();

    return () => controller.abort();
  }, [offset, selectedThreadId, fetchHistory, fetchAnalytics]);

  useEffect(() => {
    setSelectedThreadId(activeThreadId || null);
  }, [activeThreadId]);

  useEffect(() => {
    onRefreshThreads();
  }, [onRefreshThreads]);

  const deleteConversation = async (id) => {
    if (!window.confirm('Are you sure you want to delete this conversation?')) return;
    
    try {
      await axiosInstance.delete(
        `chat/history/${id}/delete/`
      );
      await fetchHistory();
      await fetchAnalytics();
      await handleRefreshThreads();
    } catch (error) {
      console.error('Error deleting conversation:', error);
    }
  };

  const deleteAllConversations = async () => {
    if (!window.confirm('Are you sure you want to delete ALL conversations? This cannot be undone.')) return;
    
    try {
      await axiosInstance.delete(
        'chat/history/delete/'
      );
      await fetchHistory();
      await fetchAnalytics();
      await handleRefreshThreads();
    } catch (error) {
      console.error('Error deleting all conversations:', error);
    }
  };

  const exportHistory = () => {
    const dataStr = JSON.stringify(conversations, null, 2);
    const dataBlob = new Blob([dataStr], { type: 'application/json' });
    const url = URL.createObjectURL(dataBlob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `chat-history-${new Date().toISOString()}.json`;
    link.click();
  };

  const formatDate = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
  };

  const handleThreadSelection = async (threadId) => {
    if (threadId === selectedThreadId) {
      return;
    }
    setSelectedThreadId(threadId);
    setOffset(0);
    await onSelectThread(threadId);
  };

  const handleCreateThread = async () => {
    const newThreadId = await onCreateThread();
    if (newThreadId) {
      setSelectedThreadId(newThreadId);
      setOffset(0);
      await onSelectThread(newThreadId);
      await fetchHistory();
      await fetchAnalytics();
    }
  };

  const handleRefreshThreads = async () => {
    await onRefreshThreads();
  };

  const deleteThread = async (threadId, event) => {
    event.stopPropagation();
    if (!window.confirm('Delete this conversation? All messages will be removed.')) return;
    
    try {
      await axiosInstance.delete(`chat/threads/${threadId}/`);
      await handleRefreshThreads();
      if (selectedThreadId === threadId) {
        setSelectedThreadId(null);
        setOffset(0);
      }
      await fetchHistory();
      await fetchAnalytics();
    } catch (error) {
      console.error('Error deleting thread:', error);
    }
  };

  const renderThreadSidebar = () => {
    if (isThreadsLoading) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-gray-400">
          <FontAwesomeIcon icon={faSpinner} spin size="2x" />
          <span className="mt-2 text-sm">Loading…</span>
        </div>
      );
    }

    if (!threads || threads.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-gray-400 px-4 text-center">
          <FontAwesomeIcon icon={faComments} size="2x" className="mb-2 opacity-50" />
          <p className="text-sm">No conversations yet</p>
        </div>
      );
    }

    return (
      <div className="space-y-1">
        <button
          onClick={() => handleThreadSelection(null)}
          className={`w-full text-left px-4 py-3 rounded-lg transition-all group ${
            selectedThreadId === null
              ? 'bg-green-50 border-l-4 border-green-600 text-green-900'
              : 'hover:bg-gray-50 text-gray-700'
          }`}
        >
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3 flex-1 min-w-0">
              <FontAwesomeIcon 
                icon={faComments} 
                className={selectedThreadId === null ? 'text-green-600' : 'text-gray-400 group-hover:text-green-500'} 
              />
              <span className="font-medium truncate">All Conversations</span>
            </div>
            {selectedThreadId === null && (
              <div className="w-2 h-2 rounded-full bg-green-600"></div>
            )}
          </div>
        </button>
        {threads.map((thread) => (
          <div
            key={thread.id}
            className={`w-full text-left px-4 py-3 rounded-lg transition-all group relative ${
              selectedThreadId === thread.id
                ? 'bg-green-50 border-l-4 border-green-600 text-green-900'
                : 'hover:bg-gray-50 text-gray-700'
            }`}
          >
            <button
              onClick={() => handleThreadSelection(thread.id)}
              className="w-full text-left"
              title={thread.title}
            >
              <div className="flex items-center justify-between">
                <div className="flex-1 min-w-0 pr-8">
                  <p className="font-medium truncate">
                    {thread.title || 'Untitled chat'}
                  </p>
                  {thread.last_message_preview && (
                    <p className="text-xs text-gray-500 truncate mt-1">
                      {thread.last_message_preview}
                    </p>
                  )}
                  {thread.message_count > 0 && (
                    <p className="text-xs text-gray-400 mt-1">
                      {thread.message_count} message{thread.message_count !== 1 ? 's' : ''}
                    </p>
                  )}
                </div>
                {selectedThreadId === thread.id && (
                  <div className="w-2 h-2 rounded-full bg-green-600 flex-shrink-0"></div>
                )}
              </div>
            </button>
            <button
              onClick={(e) => deleteThread(thread.id, e)}
              className="absolute right-2 top-3 opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all p-1.5 hover:bg-red-50 rounded"
              title="Delete thread"
            >
              <FontAwesomeIcon icon={faTrash} size="sm" />
            </button>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 z-50 flex items-center justify-center p-4 backdrop-blur-sm">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-6xl max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-green-500 to-teal-600 text-white px-6 py-4">
          <div className="flex justify-between items-center">
            <div>
              <h2 className="text-2xl font-bold flex items-center gap-3">
                <FontAwesomeIcon icon={faHistory} />
                Chat History
              </h2>
            </div>
            <div className="flex items-center gap-3">
              {/* Tabs */}
              <div className="flex gap-2">
                <button
                  onClick={() => setActiveTab('history')}
                  className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${
                    activeTab === 'history'
                      ? 'bg-white text-green-600 shadow-lg'
                      : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                  }`}
                >
                  <FontAwesomeIcon icon={faComments} className="mr-2" />
                  Chats
                </button>
                <button
                  onClick={() => setActiveTab('analytics')}
                  className={`px-4 py-2 rounded-lg transition-all text-sm font-medium ${
                    activeTab === 'analytics'
                      ? 'bg-white text-green-600 shadow-lg'
                      : 'bg-white bg-opacity-20 text-white hover:bg-opacity-30'
                  }`}
                >
                  <FontAwesomeIcon icon={faChartLine} className="mr-2" />
                  Insights
                </button>
              </div>
              <button
                onClick={onClose}
                className="text-white hover:bg-white hover:bg-opacity-20 p-2 rounded-lg transition-all"
              >
                <FontAwesomeIcon icon={faTimes} size="lg" />
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-hidden flex">
          {activeTab === 'history' ? (
            <>
              {/* Sidebar - Thread List */}
              <div className="w-80 border-r border-gray-200 bg-gray-50 flex flex-col">
                <div className="p-4 border-b border-gray-200 bg-white">
                  <div className="flex gap-2">
                    <button
                      onClick={handleCreateThread}
                      className="flex-1 px-4 py-2.5 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-all flex items-center justify-center gap-2 font-medium shadow-sm"
                    >
                      <FontAwesomeIcon icon={faPlus} />
                      New Chat
                    </button>
                    <button
                      onClick={handleRefreshThreads}
                      className="px-4 py-2.5 border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all"
                      title="Refresh"
                    >
                      <FontAwesomeIcon icon={faSyncAlt} />
                    </button>
                  </div>
                </div>
                <div className="flex-1 overflow-y-auto p-3">
                  {renderThreadSidebar()}
                </div>
              </div>

              {/* Main Content - Conversation Messages */}
              <div className="flex-1 flex flex-col bg-white">
                {/* Action Bar */}
                <div className="px-6 py-4 border-b border-gray-200 bg-gray-50">
                  <div className="flex justify-between items-center">
                    <div>
                      <h3 className="text-lg font-semibold text-gray-800">
                        {selectedThread ? selectedThread.title : 'All Conversations'}
                      </h3>
                      <p className="text-sm text-gray-500 mt-0.5">
                        {conversations.length} message{conversations.length !== 1 ? 's' : ''}
                      </p>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={exportHistory}
                        className="px-3 py-2 text-sm border border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 transition-all flex items-center gap-2"
                      >
                        <FontAwesomeIcon icon={faDownload} />
                        Export
                      </button>
                      <button
                        onClick={deleteAllConversations}
                        className="px-3 py-2 text-sm border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition-all flex items-center gap-2"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                        Delete All
                      </button>
                    </div>
                  </div>
                </div>

                {/* Messages List */}
                <div className="flex-1 overflow-y-auto p-6">
                  {loading ? (
                    <div className="flex justify-center items-center h-full">
                      <div className="text-center">
                        <FontAwesomeIcon icon={faSpinner} spin size="3x" className="text-green-500 mb-3" />
                        <p className="text-gray-500">Loading messages…</p>
                      </div>
                    </div>
                  ) : conversations.length === 0 ? (
                    <div className="flex items-center justify-center h-full text-center">
                      <div>
                        <FontAwesomeIcon icon={faComments} size="4x" className="text-gray-300 mb-4" />
                        <p className="text-gray-500 text-lg font-medium">No messages yet</p>
                        <p className="text-gray-400 text-sm mt-2">Start chatting to see your history here</p>
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-4 max-w-4xl mx-auto">
                      {conversations.map((conv) => (
                        <div
                          key={conv.id}
                          className="group"
                        >
                          <div className="flex items-center justify-between mb-2">
                            <div className="flex items-center gap-2 text-xs text-gray-400">
                              <FontAwesomeIcon icon={faCalendar} />
                              <span>{formatDate(conv.timestamp)}</span>
                            </div>
                            <button
                              onClick={() => deleteConversation(conv.id)}
                              className="opacity-0 group-hover:opacity-100 text-red-400 hover:text-red-600 transition-all text-sm"
                              title="Delete message"
                            >
                              <FontAwesomeIcon icon={faTrash} />
                            </button>
                          </div>

                          {/* User Message */}
                          <div className="flex justify-end mb-3">
                            <div className="max-w-[80%]">
                              <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl rounded-tr-sm px-4 py-3 shadow-sm">
                                <p className="text-sm leading-relaxed">{conv.user_message}</p>
                              </div>
                            </div>
                          </div>

                          {/* Bot Response */}
                          <div className="flex justify-start mb-6">
                            <div className="max-w-[80%]">
                              <div className="bg-gray-100 text-gray-800 rounded-2xl rounded-tl-sm px-4 py-3 shadow-sm">
                                <p className="text-sm leading-relaxed">{conv.bot_response}</p>
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            </>
          ) : (
            <div className="flex-1 overflow-y-auto p-6">
              <div className="max-w-5xl mx-auto space-y-6">
              {/* Analytics Dashboard */}
              {analytics ? (
                <>
                  {/* Summary Cards */}
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white rounded-xl p-6 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-blue-100 text-sm">Total Conversations</p>
                          <p className="text-4xl font-bold mt-2">{analytics.total_conversations}</p>
                        </div>
                        <FontAwesomeIcon icon={faComments} size="2x" className="opacity-50" />
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-green-500 to-green-600 text-white rounded-xl p-6 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-green-100 text-sm">This Week</p>
                          <p className="text-4xl font-bold mt-2">{analytics.recent_conversations_7days}</p>
                        </div>
                        <FontAwesomeIcon icon={faCalendar} size="2x" className="opacity-50" />
                      </div>
                    </div>

                    <div className="bg-gradient-to-br from-purple-500 to-purple-600 text-white rounded-xl p-6 shadow-lg">
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="text-purple-100 text-sm">Engagement</p>
                          <p className="text-2xl font-bold mt-2 capitalize">{analytics.engagement_trend}</p>
                        </div>
                        <FontAwesomeIcon icon={faChartLine} size="2x" className="opacity-50" />
                      </div>
                    </div>
                  </div>

                  {/* Main Topics */}
                  {analytics.main_topics && analytics.main_topics.length > 0 && (
                    <div className="bg-white border border-gray-200 rounded-xl p-6">
                      <h3 className="text-lg font-semibold text-gray-800 mb-4">Main Topics Discussed</h3>
                      <div className="flex flex-wrap gap-2">
                        {analytics.main_topics.map((topic, index) => (
                          <span
                            key={index}
                            className="px-4 py-2 bg-gradient-to-r from-green-100 to-teal-100 text-green-800 rounded-full text-sm font-medium"
                          >
                            {topic.charAt(0).toUpperCase() + topic.slice(1)}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* Last Conversation Date */}
                  <div className="bg-white border border-gray-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-gray-800 mb-2">Last Conversation</h3>
                    <p className="text-gray-600">
                      {formatDate(analytics.last_conversation_date)}
                    </p>
                  </div>

                  {/* Insights */}
                  <div className="bg-gradient-to-br from-amber-50 to-orange-50 border border-amber-200 rounded-xl p-6">
                    <h3 className="text-lg font-semibold text-amber-900 mb-3">Insights</h3>
                    <ul className="space-y-2 text-amber-800">
                      <li>• You've had {analytics.total_conversations} meaningful conversations with SereniAI</li>
                      <li>• You're actively engaging with {analytics.recent_conversations_7days} chats this week</li>
                      {analytics.engagement_trend === 'increasing' && (
                        <li>• Your engagement is increasing - keep up the great work! 🌟</li>
                      )}
                      <li>• Regular check-ins help track your mental wellness journey</li>
                    </ul>
                  </div>
                </>
              ) : (
                <div className="text-center py-12 text-gray-500">
                  <p>No analytics data available yet</p>
                </div>
              )}
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default ChatHistory;
